<template>
  <div class="ds-background-color-grey-100">
    <div class="ds-container ds-md-only-padding-x-24">
      <div class="ds-grid">
        <!-- TODO: Add support for filters section expand / collapse in small screens -->
        <div class="ds-grid__col ds-grid__col--12 ds-md-grid__col--6 ds-lg-grid__col--3 ds-margin-bottom-24">
          <DsSelectBox
            :labels="computedLabels.topics"
            :icon="defaultIcons.topics"
            :placeholder="computedLabels.topics.label"
            :options="filterOptions.topics"
            :value="getFilterValue('topics', true, initialFilters)"
            multiple
            class="ds-width-full"
            @value="handleFilterUpdate('topics', $event)"
          />
        </div>

        <div class="ds-grid__col ds-grid__col--12 ds-md-grid__col--6 ds-lg-grid__col--3 ds-margin-bottom-24">
          <DsSelectBox
            :labels="computedLabels.articleType"
            :icon="defaultIcons.articleType"
            :placeholder="computedLabels.articleType.label"
            :options="filterOptions.articleType"
            :value="getFilterValue('articleType', true, initialFilters)"
            class="ds-width-full"
            @value="handleFilterUpdate('articleType', $event)"
          />
        </div>

        <div class="ds-grid__col ds-grid__col--12 ds-md-grid__col--6 ds-lg-grid__col--3 ds-margin-bottom-24">
          <DsSelectBox
            :labels="computedLabels.industries"
            :icon="defaultIcons.industries"
            :placeholder="computedLabels.industries.label"
            :options="filterOptions.industries"
            :value="getFilterValue('industries', true, initialFilters)"
            multiple
            class="ds-width-full"
            @value="handleFilterUpdate('industries', $event)"
          />
        </div>

        <div class="ds-grid__col ds-grid__col--12 ds-md-grid__col--6 ds-lg-grid__col--3 ds-margin-bottom-24">
          <DsSelectBox
            :labels="computedLabels.products"
            :icon="defaultIcons.products"
            :placeholder="computedLabels.products.label"
            :options="filterOptions.products"
            :value="getFilterValue('products', true, initialFilters)"
            multiple
            class="ds-width-full"
            @value="handleFilterUpdate('products', $event)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { DsSelectBox } from 'awds'
import copy from 'fast-copy'
import { useTrackingUtils } from '../../../core/utils/useTrackingUtils'

const emit = defineEmits(['selected'])
const { getFilterValue, normalizeKnowledgeHubFilterOptions, setFilter, filters, handleSearch, query, resetFilters } =
  useFilters(emit)
const { labels, fetchGlobalDataForContentType, localeGlobalContentData, globalContentData } =
  useKnowledgeHubGlobalData()
const { trackEvent, filterTrackingOptions, filterItemTrackingData } = useTrackingUtils()

// Should not be blocking
// await fetchGlobalDataForContentType('PageKnowledgeHub')

const props = defineProps({
  initialFilters: {
    type: Array,
    default: () => []
  },
  forcedReset: {
    type: Boolean,
    default: false
  }
})

const defaultIcons = ref({
  topics: { name: 'target', category: 'various' },
  articleType: { name: 'document-stack', category: 'files' },
  industries: { name: 'bag', category: 'various' },
  products: { name: 'product-adyen', category: 'products-24' }
})

const previousState = ref(copy(props.initialFilters))

const computedLabels = computed(() => {
  const allLabels = labels.value
  const basicLabels = {
    ariaLabelHint: allLabels.ariaLabelArrowNavigationHint,
    clearValueButton: allLabels.clearValueButton,
    submitValueButton: allLabels.submitValueButton
  }
  return {
    topics: {
      label: allLabels.topicFilter,
      results: allLabels.topicFilter?.toLowerCase(),
      ...basicLabels
    },
    articleType: {
      label: allLabels.articleTypeFilter,
      ...basicLabels
    },
    industries: {
      label: allLabels.industryFilter,
      results: allLabels.industryFilter?.toLowerCase(),
      ...basicLabels
    },
    products: {
      label: allLabels.productFilter,
      results: allLabels.productFilter?.toLowerCase(),
      ...basicLabels
    }
  }
})

const filterOptions = computed(() => {
  const icons = defaultIcons.value
  return {
    topics: normalizeKnowledgeHubFilterOptions(
      localeGlobalContentData.value.globalDataTopics,
      `${icons.topics.category}--${icons.topics.name}`
    ),
    articleType: normalizeKnowledgeHubFilterOptions(
      localeGlobalContentData.value.globalDataArticleTypes,
      `${icons.articleType.category}--${icons.articleType.name}`
    ),
    industries: normalizeKnowledgeHubFilterOptions(
      localeGlobalContentData.value.globalDataIndustries,
      `${icons.industries.category}--${icons.industries.name}`
    ),
    products: normalizeKnowledgeHubFilterOptions(
      localeGlobalContentData.value.globalDataProducts,
      `${icons.products.category}--${icons.products.name}`
    )
  }
})

watch(props.initialFilters, (newValue, oldValue) => {
  if ((!oldValue || oldValue.length === 0) && newValue) {
    filters.value = newValue
  }
})

// TODO - Check if resetFilter composable shares filters state (in composable)
onMounted(() => {
  if (props.forcedReset) {
    filters.value = []
  }
})

const handleFilterUpdate = (filterName, filterValue) => {
  setFilter(filterName, filterValue)

  trackEvent(
    filterItemTrackingData({
      filterName,
      filterValue,
      isValueId: true,
      componentType: filterTrackingOptions.componentType.knowledgeHub,
      filterType: filterTrackingOptions.filterType.dropdown,
      filterOptions: filterOptions.value[filterName],
      previousFilterState: previousState.value.find((item) => item.name === filterName)?.value || []
    })
  )

  previousState.value = copy(filters.value)
  emit('selected', filters.value)
}
</script>
